import { BigNumber, Contract, PayableOverrides, providers, Signer, utils } from 'ethers';
import Contracts from '@tokenysolutions/t-rex';

import { InvalidProviderError } from '../utils/errors/invalid-provider.error';
import { deployDVD } from '../contract-methods/dvd.methods';
import { OverrideTransactionsInterface } from '../utils/utils';
import { ContractNotDeployingError } from '../utils/errors/contract-not-deploying.error';

type TransferFees = {
  counterpartFee: BigNumber;
  recipientForCounterpartFees: string;
  recipientForSenderFees: string;
  senderFee: BigNumber;
};

type Fees = {
  counterpartFee: BigNumber;
  recipientForCounterpartFees: string;
  recipientForSenderFees: string;
  senderFee: BigNumber;
  feeBase: BigNumber;
};

const dvdInterface = new utils.Interface([
  'event DVDTransferInitiated(bytes32 indexed transferID, address maker, address indexed token1, uint256 token1Amount, address taker, address indexed token2, uint256 token2Amount)',
]);

/**
 * A class to manage and interact with a DVD Transfer smart contract on-chain.
 * A DVD Transfer smart-contract supports multiple pairs of ERC20 tokens and T-REX tokens.
 *
 * @example
 * // Instantiate an existing DVD contract with
 * await DVD.at(<address>);
 *
 * @example
 * // Deploy a new DVD contract. The signer would be the owner of the contract and allowed to update fees.
 * await DVD.deploy(signer).then(dvd => dvd.deployed());
 */
export class DVD {
  public address: string;
  public providerOrSigner?: Signer | providers.Provider;
  public deployTransaction?: providers.TransactionResponse;

  private constructor({
    address,
    deployTransaction,
    providerOrSigner,
  }: {
    address: string;
    deployTransaction?: providers.TransactionResponse;
    providerOrSigner?: Signer | providers.Provider;
  }) {
    this.address = address;
    this.deployTransaction = deployTransaction;
    this.providerOrSigner = providerOrSigner;
  }

  /**
   * Instantiate an existing DVD transfer contract by its address or ENS name.
   * @param address Address or ENS
   * @param providerOrSigner Required as a provider or a signer with a provider if using ENS resolution.
   *
   * @example
   * // Via ENS
   * const dvd = await DVD.at('dvd.tokeny.eth', provider);
   *
   * @example
   * // Via address
   * const dvd = await DVD.at('0x...');
   */
  static async at(address: string, providerOrSigner?: Signer | providers.Provider): Promise<DVD> {
    if (providerOrSigner && !Signer.isSigner(providerOrSigner) && !providers.Provider.isProvider(providerOrSigner)) {
      throw new InvalidProviderError({ message: 'providerOrSigner must be a provider or a signer.' });
    }

    let _address: string;
    if (!utils.isAddress(address)) {
      if (!providers.Provider.isProvider(providerOrSigner)) {
        throw new InvalidProviderError({ message: 'Resolving an name on ENS requires a provider.' });
      }
      _address = (await providerOrSigner.resolveName(address)) || address;
    } else {
      _address = address;
    }

    return new DVD({ address: _address, providerOrSigner });
  }

  /**
   * Deploy a new DVD transfer contract.
   * @param signer
   * @param transactionOverride
   *
   * @example
   * const dvd = await DVD.deploy(signer);
   * await dvd.deployed();
   */
  static async deploy(signer: Signer, transactionOverride: OverrideTransactionsInterface = {}): Promise<DVD> {
    if (!Signer.isSigner(signer)) {
      throw new InvalidProviderError({ message: 'Deploying a DVD contract require a Signer.' });
    }

    const contract = await deployDVD({
      signer,
      overrides: transactionOverride,
    });

    return new DVD({
      address: contract.address,
      deployTransaction: contract.deployTransaction,
      providerOrSigner: signer,
    });
  }

  /**
   * Computes the ID of a parity.
   *
   * @param senderToken
   * @param counterpartToken
   */
  static parityId(senderToken: string, counterpartToken: string): string {
    return utils.keccak256(utils.defaultAbiCoder.encode(['address', 'address'], [senderToken, counterpartToken]));
  }

  /**
   * Retrieve a transfer ID from a transaction receipt.
   * The transaction must include a call to initiate a DVD transfer.
   *
   * @param receipt
   */
  static getTransferIdFromReceipt(receipt: providers.TransactionReceipt): string | null {
    const eventLog = receipt.logs.find((event) => event.topics[0] === dvdInterface.getEventTopic('DVDTransferInitiated'));
    if (!eventLog) {
      throw new Error('This transaction did not emit a DVDTransferInitiated event.');
    }

    const eventLogDetails = dvdInterface.parseLog(eventLog);
    return eventLogDetails.args.transferID;
  }

  /**
   * Await the deployment transaction. Can only be called after the method `DVD.deploy()`.
   * @param confirmations
   */
  async deployed(confirmations?: number): Promise<providers.TransactionReceipt> {
    if (!this.deployTransaction) {
      throw new ContractNotDeployingError();
    }

    return this.deployTransaction.wait(confirmations);
  }

  /**
   * Register a new DVD transfer on the DVD contract, from the address of the signer, targeting the counterpart wallet.
   * The transfer is between senderAmount of senderToken from signer address wallet, and counterpartAmount of counterpartToken from counterpart wallet.
   *
   * The wallet of the signer must have at least senderAmount of senderToken when the transaction is created or it will fail.
   * The DVD transfer contract must have an allowance of at least senderAmount for the senderToken.
   * The counterpartToken must be an ERC20-compatible token.
   *
   * This method should emit a `DVDTransferInitiated` on-chain event (should the contract implements it).
   *
   * @param counterpart
   * @param counterpartAmount
   * @param counterpartToken
   * @param senderAmount
   * @param senderToken
   * @param overrides
   * @param signer If not specified, the signer would be the signer instantiated with the DVD if any.
   */
  async initiate(
    {
      counterpart,
      counterpartAmount,
      counterpartToken,
      senderAmount,
      senderToken,
    }: {
      counterpart: string;
      counterpartAmount: BigNumber;
      counterpartToken: string;
      senderAmount: BigNumber;
      senderToken: string;
    },
    { overrides = {}, signer }: { overrides?: PayableOverrides; signer?: Signer } = {},
  ): Promise<providers.TransactionResponse> {
    const _signer = this.getSigner(signer);

    const contract = new Contract(
      this.address,
      ['function initiateDVDTransfer(address _token1, uint256 _token1Amount, address _counterpart, address _token2, uint256 _token2Amount)'],
      _signer,
    );

    return contract.initiateDVDTransfer(senderToken, senderAmount, counterpart, counterpartToken, counterpartAmount, overrides);
  }

  /**
   * Delete a pending DVD transfer that was previously initiated via the `initiate` method.
   *
   * The wallet of the signer must be either the taker of the maker of the initiated transfer, the owner of the DVD
   * transfer contract, or a TREX Agent if one TREX token is involved in the transfer. These checks might have
   * additional rules depending on the implementing contract.
   *
   * This method should emit a `DVDTransferCancelled` on-chain event (should the contract implements it).
   *
   * @param transferId
   * @param overrides
   * @param signer If not specified, the signer would be the signer instantiated with the DVD if any.
   */
  async cancel(transferId: string, { overrides = {}, signer }: { overrides?: PayableOverrides; signer?: Signer } = {}): Promise<providers.TransactionResponse> {
    const _signer = this.getSigner(signer);

    const contract = new Contract(this.address, ['function cancelDVDTransfer(bytes32 _transferID)'], _signer);

    return contract.cancelDVDTransfer(transferId, overrides);
  }

  /**
   * Execute a pending DVD transfer on the DVD contract transfer manager.
   * Depending on the implementation, parties may receive less than the predicted amount, should fees be declared
   * in the transfer manager contract, they would be taken on these amounts.
   *
   * The wallet of the counterpart (or taker, as declared when the transfere was created), must have enough
   * tokens in balance to proceed to the transfer.
   * The DVD transfer manager contract must have the allowance on the amount of token to transfer from thee wallet of
   * the counterpart.
   *
   * The wallet of the signer must be either the taker (counterpart) of the transfer or an agent of any T-REX token
   * involved in the transfer.
   *
   * @param transferId
   * @param overrides
   * @param signer
   */
  async take(transferId: string, { overrides = {}, signer }: { overrides?: PayableOverrides; signer?: Signer } = {}): Promise<providers.TransactionResponse> {
    const _signer = this.getSigner(signer);

    const contract = new Contract(this.address, ['function takeDVDTransfer(bytes32 _transferID)'], _signer);

    return contract.takeDVDTransfer(transferId, overrides);
  }

  /**
   * Modify the fees applied to a pair of tokens on the DVD transfer manager contract.
   *
   * The wallet of the signer must be either the owner of the DVD manager contract or an agent of any of the T-REX tokens
   * involved in the pair.
   *
   * This method should emit a `FeeModified` event, should the contract implement it.
   *
   * @param counterpartFee The fee to apply to the counterpart amount (The quantity would be counterpartAmount * counterpartFee / 10^feeBase).
   * @param counterpartToken The address of the counterpart token for the parity sender / counterpart.
   * @param feeBase Must be higher or equal to 2 (precision 10^2), and should exceed 5 (precision 5^10) to avoid overflows. Set to 2 for having fees calculated as a percentage (10^2).
   * @param recipientOfCounterpartFees The address where fees would be send from the counterpart amount transferred. Cannot be the null address if counterpartFee is not 0.
   * @param recipientOfSenderFees The address where fees would be send from the sender amount transferred. Cannot be the null address if senderFee is not 0.
   * @param senderFee The fee to apply to the sender amount (The quantity would be senderAmount * senderFee / 10^feeBase).
   * @param senderToken The address of the base token for the parity sender / counterpart.
   * @param overrides
   * @param signer
   */
  async modifyFee(
    {
      counterpartFee,
      counterpartToken,
      feeBase,
      recipientOfCounterpartFees,
      recipientOfSenderFees,
      senderFee,
      senderToken,
    }: {
      counterpartFee: BigNumber;
      counterpartToken: string;
      feeBase: BigNumber;
      recipientOfCounterpartFees: string;
      recipientOfSenderFees: string;
      senderFee: BigNumber;
      senderToken: string;
    },
    { overrides = {}, signer }: { overrides?: PayableOverrides; signer?: Signer } = {},
  ): Promise<providers.TransactionResponse> {
    const _signer = this.getSigner(signer);

    const contract = new Contract(
      this.address,
      ['function modifyFee(address _token1, address _token2, uint _fee1, uint _fee2, uint _feeBase, address _fee1Wallet, address _fee2Wallet)'],
      _signer,
    );

    return contract.modifyFee(senderToken, counterpartToken, senderFee, counterpartFee, feeBase, recipientOfSenderFees, recipientOfCounterpartFees, overrides);
  }

  /**
   * Calculates the fees to apply to a specific transfer depending on the fees applied to the parity used in the transfer
   * @param transferId Must exist on the DVD contract, a transfer must already have been initialized.
   * @param provider
   * @returns The fees to apply on each prat of the transfer.
   */
  async calculateFee(
    transferId: string,
    { overrides = {}, provider }: { overrides?: PayableOverrides; provider?: providers.Provider } = {},
  ): Promise<TransferFees> {
    const _provider = this.getProvider(provider);

    const contract = new Contract(this.address, ['function calculateFee(bytes32 _transferID) public view returns(TxFees memory)'], _provider);

    return contract.calculateFee(transferId, overrides);
  }

  /**
   * Returns the owner of the DVD manager contract.
   * @param provider
   */
  async getOwner({ overrides = {}, provider }: { overrides?: PayableOverrides; provider?: providers.Provider } = {}): Promise<string> {
    const _provider = this.getProvider(provider);

    const contract = new Contract(this.address, ['function owner() public view returns(address)'], _provider);

    return contract.owner(overrides);
  }

  /**
   * Get the fees defined for a pair. Use the static method `parityId()` to compute the ID of a parity.
   * @param parityId
   * @param provider
   */
  async getFee(parityId: string, { overrides = {}, provider }: { overrides?: PayableOverrides; provider?: providers.Provider } = {}): Promise<Fees> {
    const _provider = this.getProvider(provider);

    const contract = new Contract(
      this.address,
      ['struct Fee { uint token1Fee; uint token2Fee; uint feeBase; address fee1Wallet; address fee2Wallet; }', 'function fee() public view returns(Fee)'],
      _provider,
    );

    const fees = await contract.fee(parityId, overrides);

    return {
      feeBase: fees.feebase,
      counterpartFee: fees.token2Fee,
      recipientForCounterpartFees: fees.fee2Wallet,
      recipientForSenderFees: fees.fee1Wallet,
      senderFee: fees.token1Fee,
    };
  }

  getInstance(providerOrSigner?: Signer | providers.Provider): Contract {
    return new Contract(this.address, Contracts.contracts.DVDTransferManager.abi, providerOrSigner);
  }

  /**
   * Validate if a signer provided is indeed a signer and return it,
   * or return the signer of the DVD instance if it is a signer.
   * @param signer
   * @private
   */
  private getSigner(signer?: Signer): Signer {
    if (signer) {
      if (!Signer.isSigner(signer)) {
        throw new InvalidProviderError({
          message: 'DVD was instantiated without a signer. Specify a signer in the options of the method or instantiate the DVD with a signer.',
        });
      }
      return signer;
    } else {
      if (!Signer.isSigner(this.providerOrSigner)) {
        throw new InvalidProviderError({
          message: 'DVD was instantiated without a signer. Specify a signer in the options of the method or instantiate the DVD with a signer.',
        });
      }
      return this.providerOrSigner;
    }
  }

  /**
   * Validate if a provider is indeed a provider and return it,
   * or return the provider of the DVD instance if it is a provider.
   * @param provider
   * @private
   */
  private getProvider(provider?: providers.Provider): providers.Provider {
    if (provider) {
      if (!providers.Provider.isProvider(provider)) {
        throw new InvalidProviderError({
          message: 'DVD was instantiated without a provider. Specify a provider in the options of the method or instantiate the DVD with a provider.',
        });
      }
      return provider;
    } else if (providers.Provider.isProvider(this.providerOrSigner)) {
      return this.providerOrSigner;
    } else if (Signer.isSigner(this.providerOrSigner) && providers.Provider.isProvider(this.providerOrSigner.provider)) {
      return this.providerOrSigner.provider;
    } else {
      throw new InvalidProviderError({
        message: 'DVD was instantiated without a provider. Specify a provider in the options of the method or instantiate the DVD with a provider.',
      });
    }
  }
}
